<template>
  <div class="seckill-layout">
    <Header />
    <div class="seckill-page main-1200-width padding-bottom-40">
    <div class="seckill-shell">
      <header class="seckill-page-head">
        <div class="seckill-page-head-left">
          <div class="seckill-page-icon" aria-hidden="true" />
          <div class="seckill-page-titles">
            <h1 class="seckill-page-title">秒杀专区</h1>
            <p class="seckill-page-sub">精选秒杀商品 · 先到先得</p>
          </div>
        </div>
        <p class="seckill-page-hint">限时抢购，库存有限</p>
      </header>

      <div class="seckill-page-loading" v-if="loading">加载中…</div>
      <div class="seckill-page-empty" v-else-if="!items.length">暂无秒杀活动</div>

      <div class="seckill-grid" v-else>
        <div
          class="seckill-card"
          v-for="it in items"
          :key="`${it.activity_id}-${it.goods.id}`"
          @click="goGoods(it.activity_id, it.goods.id)"
        >
          <div class="seckill-card-cover">
            <img class="seckill-card-img" :src="$url.cdn(it.goods.image)" :alt="it.goods.title" />
            <div class="seckill-card-badge">{{ it.activity_title }}</div>
            <div class="seckill-card-countdown">{{ remainText(it.activity) }}</div>
          </div>
          <div class="seckill-card-body">
            <div class="seckill-card-name" :title="it.goods.title">{{ it.goods.title }}</div>
            <div class="seckill-card-price-row" v-if="is_login">
              <div class="seckill-card-price-line">
                <span class="seckill-card-price-tag">秒杀价</span>
                <span class="seckill-card-price">
                  <span class="seckill-card-price-y">¥</span>{{ formatSeckillGoodsPrice(it.goods) }}
                </span>
              </div>
              <div v-if="seckillRefText(it.goods)" class="seckill-card-ref-line">
                <span class="seckill-card-ref-label">日常价</span>
                <span class="seckill-card-ref">¥{{ seckillRefText(it.goods) }}</span>
              </div>
            </div>
            <div v-else class="seckill-card-price-row">
              <span class="seckill-card-price seckill-card-price--mask">¥ ****</span>
            </div>
            <span class="seckill-card-action">去秒杀</span>
          </div>
        </div>
      </div>
    </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Header from '@/globalComponents/Header'
import Footer from '@/globalComponents/Footer'
import activityApi from '@/api/activity.js'
import goodsApi from '@/api/goods.js'
import { isSeckillActivityLive } from '@/units/seckillLive.js'
import {
  formatSeckillActivityPriceOnlyDisplay,
  formatSeckillCatalogFloorLineDisplay,
  getSeckillRefPriceText,
} from '@/units/activityGoodsPrice.js'
import {
  enrichGoodsListWithActivityDetail,
  mapWithConcurrency,
  normalizeActivityListRows,
} from '@/units/enrichSeckillGoodsFromDetail.js'

export default {
  data() {
    return {
      nowTs: Math.floor(Date.now() / 1000),
      timerId: null,
      loading: false,
      activities: [],
      goodsMap: {}, // activity_id -> goods[]
    }
  },
  computed: {
    ...mapGetters(['is_login']),
    items() {
      const now = Number(this.nowTs || 0)
      const items = []
      for (const a of this.activities) {
        if (!a || !a.id) continue
        if (!isSeckillActivityLive(a, now)) continue
        const goodsList = this.goodsMap[String(a.id)] || []
        for (const g of goodsList) {
          items.push({
            activity_id: a.id,
            activity: a,
            activity_title: a.title || `秒杀活动 #${a.id}`,
            goods: g,
          })
        }
      }
      return items
    },
  },
  created() {
    this.timerId = setInterval(() => {
      this.nowTs = Math.floor(Date.now() / 1000)
    }, 1000)
    this.load()
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId)
  },
  methods: {
    formatPrice(e) {
      return e && e.length === 1 ? e[0] : (e || []).join('~')
    },
    formatSeckillGoodsPrice(goods) {
      const act = formatSeckillActivityPriceOnlyDisplay(goods)
      return act || '—'
    },
    seckillRefText(goods) {
      return getSeckillRefPriceText(goods) || formatSeckillCatalogFloorLineDisplay(goods)
    },
    remainText(activity) {
      if (!activity) return ''
      const end = Number(activity.end_time || 0)
      const now = Number(this.nowTs || 0)
      const remain = end - now
      if (!end || !Number.isFinite(remain) || remain <= 0) return '活动已结束'
      const h = Math.floor(remain / 3600)
      const m = Math.floor((remain % 3600) / 60)
      const s = Math.floor(remain % 60)
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
      return `距结束 ${pad(h)}:${pad(m)}:${pad(s)}`
    },
    async loadGoods(activityId) {
      const key = String(activityId)
      if (this.goodsMap[key] && this.goodsMap[key].length) return
      const { data: goodsData } = await goodsApi.activityList({
        activity_id: activityId,
        page: 1,
        list_rows: 50,
      })
      const pageData = goodsData || {}
      const rows = normalizeActivityListRows(pageData.data || [])
      this.$set(this.goodsMap, key, rows)
      this.enrichActivityGoodsInBackground(key, activityId, rows)
    },
    async enrichActivityGoodsInBackground(key, activityId, rows) {
      try {
        const enriched = await enrichGoodsListWithActivityDetail(rows, activityId, 4)
        if (this.goodsMap[key] === rows) {
          this.$set(this.goodsMap, key, enriched)
        }
      } catch (e) {
        /* 保留列表已展示的数据 */
      }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await activityApi.list({ type: ['seckill'], list_rows: 50 })
        const list = (data && (data.data || data.list)) || []
        if (!Array.isArray(list) || !list.length) {
          this.activities = []
          this.goodsMap = {}
          return
        }
        const now = Math.floor(Date.now() / 1000)
        const picked = list.filter((x) => isSeckillActivityLive(x, now)).slice(0, 50)
        this.activities = picked
        this.goodsMap = {}
        await mapWithConcurrency(
          picked,
          (a) => (a && a.id ? this.loadGoods(a.id) : Promise.resolve()),
          4
        )
      } catch (e) {
        this.activities = []
        this.goodsMap = {}
      } finally {
        this.loading = false
      }
    },
    goGoods(activityId, goodsId) {
      this.$router.push({
        name: 'GoodsDetail',
        query: { id: goodsId, activity_id: activityId }
      })
    }
  },
  components: {
    Header,
    Footer,
  },
}
</script>

<style lang="less" scoped>
.seckill-layout {
  min-height: 100vh;
  background: #f5f5f5;
}

.seckill-page {
  padding-top: 24px;
}

.seckill-shell {
  padding: 28px 28px 32px;
  box-sizing: border-box;
  border-radius: 16px;
  background: linear-gradient(145deg, #fff8f6 0%, #ffffff 42%, #fffbfb 100%);
  border: 1px solid rgba(202, 20, 29, 0.12);
  box-shadow: 0 8px 32px rgba(202, 20, 29, 0.07);
}

.seckill-page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.seckill-page-head-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.seckill-page-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ca141d 55%, #a30f18 100%);
  box-shadow: 0 4px 14px rgba(202, 20, 29, 0.35);
  position: relative;

  &::after {
    content: '秒';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 1px;
  }
}

.seckill-page-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  line-height: 1.25;
  letter-spacing: 0.5px;
}

.seckill-page-sub {
  margin: 4px 0 0;
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.4;
}

.seckill-page-hint {
  margin: 0;
  font-size: 13px;
  color: #bfbfbf;
  white-space: nowrap;
}

.seckill-page-loading,
.seckill-page-empty {
  padding: 56px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 12px;
  border: 1px dashed #e8e8e8;
}

/* auto-fill：商品少时自动拉满一行，减少右侧大块留白 */
.seckill-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(216px, 1fr));
  gap: 20px;
  width: 100%;
}

.seckill-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.1);
    border-color: rgba(202, 20, 29, 0.25);
  }
}

.seckill-card-cover {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #f7f7f7;
}

.seckill-card-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.seckill-card-badge {
  position: absolute;
  left: 10px;
  top: 10px;
  z-index: 1;
  max-width: 72%;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.3;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #ff4d4f, #f5222d);
  box-shadow: 0 2px 8px rgba(245, 34, 45, 0.35);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seckill-card-countdown {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 1;
  max-width: 88%;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.3;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 0, 0, 0.58);
  backdrop-filter: blur(4px);
}

.seckill-card-body {
  padding: 14px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
}

.seckill-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.45;
  min-height: 40px;
  max-height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.seckill-card-price-row {
  min-height: 28px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.seckill-card-price-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
}

.seckill-card-price-tag {
  flex-shrink: 0;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: #fff;
  background: linear-gradient(90deg, #ff4d4f, #ca141d);
  border-radius: 4px;
}

.seckill-card-ref-line {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 13px;
  color: #8c8c8c;
}

.seckill-card-ref-label {
  font-size: 12px;
}

.seckill-card-ref {
  color: #bfbfbf;
  font-weight: 500;
  text-decoration: line-through;
  text-decoration-color: #bfbfbf;
  -webkit-text-decoration-line: line-through;
}

.seckill-card-price {
  font-size: 22px;
  font-weight: 800;
  color: #ca141d;
  letter-spacing: -0.5px;

  &--mask {
    font-size: 18px;
    letter-spacing: 2px;
  }
}

.seckill-card-price-y {
  font-size: 16px;
  font-weight: 700;
  margin-right: 2px;
}

.seckill-card-action {
  align-self: flex-start;
  margin-top: 2px;
  padding: 8px 20px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-align: center;
  background: linear-gradient(90deg, #ff4d4f 0%, #ca141d 100%);
  box-shadow: 0 4px 12px rgba(202, 20, 29, 0.35);
  transition: opacity 0.15s ease, transform 0.15s ease;
  user-select: none;
  pointer-events: none;
}

.seckill-card:hover .seckill-card-action {
  opacity: 0.95;
  transform: scale(1.02);
}

@media (max-width: 768px) {
  .seckill-shell {
    padding: 18px 14px 22px;
  }

  .seckill-page-title {
    font-size: 20px;
  }

  .seckill-page-icon {
    width: 40px;
    height: 40px;

    &::after {
      font-size: 17px;
    }
  }

  .seckill-page-hint {
    width: 100%;
    text-align: left;
  }

  .seckill-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }
}
</style>
